import { AdminRoles, Collaborator, DSITJob } from "hassibot/services_v2/common/types";
import { Proclient } from "hassibot/services_v2/proclient/types";

import { Caregiver } from "../services_v2/caregiver/types";

export enum FeatureFlags {
  DEBUG = "debug",
  HASSIBOT_ALL_TABS_UPDATE = "hassibot_all_tabs_update",
  // STAFFING
  SATISFACTION = "satisfaction",
  // Ouverture de ville
  MONET = "monet",
  PAYFLIX = "payflix",

  // Feature team
  SHARED_HOUSE_PLANNING_SPLITTING = "shared_house_planning_splitting",
  CONTRACTOR_CONTRACTS_GENERATION = "contractor_contracts_generation",
  CONTRACTOR_MONET = "contractor_monet",
  CONTRACTOR_MONET_SETTINGS = "contractor_monet_settings",

  // Acquisitions
  VEB_EVALUATION_SUGGESTION = "veb_evaluation_suggestion",
  INSIDE_SALES_DASHBOARD = "inside_sales_dashboard",
  INSIDE_SALES_NOTIFICATIONS = "inside_sales_notifications",
  MONET_NOTIFY_OTHER_MAILS = "monet_notify_other_mails",
  ORIGIN_PRESCRIBER_LEAD_MANUAL_OPS_TRANSFER = "origin_prescriber_lead_manual_ops_transfer",

  // Optim'ops
  GEOLOCATION = "geolocation_prod",

  // Offre
  ICOPE_VQ = "icope_vq",
}

type OhPermission = "readOnly" | "readWrite";
export type OhAccess = { hasAccess: boolean; permission?: OhPermission };
export class BaseRole {
  collaboratorIsDev(collaborator: Collaborator): boolean {
    return !!collaborator.dsitJob && [DSITJob.tech, DSITJob.em].includes(collaborator.dsitJob);
  }
  collaboratorIsProduct(collaborator: Collaborator): boolean {
    return !!collaborator.dsitJob && [DSITJob.produit, DSITJob.cpo].includes(collaborator.dsitJob);
  }

  collaboratorIsData(collaborator: Collaborator): boolean {
    return (
      !!collaborator.dsitJob &&
      [DSITJob.lead_data_analyst, DSITJob.data_analyst].includes(collaborator.dsitJob)
    );
  }
  collaboratorIsFounder(collaborator: Collaborator): boolean {
    return !!collaborator.dsitJob && collaborator.dsitJob === DSITJob.founder;
  }
  collaboratorIsCFO(collaborator: Collaborator): boolean {
    return !!collaborator.dsitJob && collaborator.dsitJob === DSITJob.cfo;
  }
  collaboratorIsDeputyCOO(collaborator: Collaborator): boolean {
    return !!collaborator.dsitJob && collaborator.dsitJob === DSITJob.deputy_coo;
  }
  collaboratorIsCMO(collaborator: Collaborator): boolean {
    return !!collaborator.dsitJob && collaborator.dsitJob === DSITJob.cmo;
  }
  collaboratorIsZoneManager(collaborator: Collaborator): boolean {
    return (
      !!collaborator.dsitJob &&
      (collaborator.dsitJob === DSITJob.rz_dev ||
        collaborator.dsitJob === DSITJob.rz_ops ||
        collaborator.dsitJob === DSITJob.rz)
    );
  }
  collaboratorIsNetworkDirector(collaborator: Collaborator): boolean {
    return !!collaborator.dsitJob && collaborator.dsitJob === DSITJob.network_director;
  }
  collaboratorIsDR(collaborator: Collaborator): boolean {
    return !!collaborator.dsitJob && collaborator.dsitJob === DSITJob.dr;
  }
  collaboratorIsDRJoya(collaborator: Collaborator): boolean {
    return !!collaborator.dsitJob && collaborator.dsitJob === DSITJob.dr_joya;
  }
  collaboratorIsAdmin(collaborator: Collaborator): boolean {
    return !!collaborator.dsitJob && AdminRoles.includes(collaborator.dsitJob);
  }
  collaboratorIsCEU(collaborator: Collaborator): boolean {
    return !!collaborator.dsitJob && collaborator.dsitJob === DSITJob.ceu;
  }
}

export const haveAccessToFeature = (
  currentCollaborator: Collaborator | null,
  feature: FeatureFlags
): boolean => {
  if (!currentCollaborator) {
    return false;
  }
  const role = new BaseRole();
  const haveFeatureFlag =
    currentCollaborator.featureFlags && currentCollaborator.featureFlags.includes(feature);

  const haveRole =
    role.collaboratorIsDev(currentCollaborator) ||
    role.collaboratorIsProduct(currentCollaborator) ||
    role.collaboratorIsData(currentCollaborator) ||
    role.collaboratorIsCMO(currentCollaborator) ||
    role.collaboratorIsDeputyCOO(currentCollaborator);

  return haveFeatureFlag || haveRole;
};

export const hasCaregiverFeatureAccess = (
  caregiver: Caregiver | null,
  feature: FeatureFlags
): boolean =>
  !!caregiver &&
  (caregiver.isTestCaregiver ||
    (caregiver.featureFlags && caregiver.featureFlags.includes(feature)));

export const hasProclientFeatureAccess = (proclient: Proclient, feature: FeatureFlags): boolean =>
  proclient?.featureFlags && proclient.featureFlags.includes(feature);

export const hasContractorMonetSettingsAccess = (
  currentCollaborator: Collaborator | null,
  isSandbox: boolean
): boolean =>
  isSandbox || haveAccessToFeature(currentCollaborator, FeatureFlags.CONTRACTOR_MONET_SETTINGS);

export const canAccessContractorMonet = (currentCollaborator: Collaborator | null): boolean => {
  if (!currentCollaborator) {
    return false;
  }
  const role = new BaseRole();

  const supportRole =
    role.collaboratorIsFounder(currentCollaborator) ||
    role.collaboratorIsAdmin(currentCollaborator) ||
    role.collaboratorIsCEU(currentCollaborator);

  return supportRole || haveAccessToFeature(currentCollaborator, FeatureFlags.CONTRACTOR_MONET);
};
